import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useCallback } from "react";

import type { RouterContextType } from "@/api/permissions/permissions.api";
import { MODULE_KEYS_MAP } from "@/api/permissions/permissions.types";
import {
  FilesHub,
  parseSectionKey,
  type SectionKey,
} from "@/components/features/FileManager/hub/FilesHub";
import { useBreadcrumb } from "@/hooks/useBreadcrumb";
import { authGuardWithPermissions } from "@/utils/authGuard";
import { getRouteIcon } from "@/utils/getRouteIconComponent";

import styles from "./filesPage.module.scss";

interface FilesSearch {
  section?: SectionKey;
  share?: string;
}

const optionalString = (value: unknown) => (typeof value === "string" && value ? value : undefined);

export const Route = createFileRoute("/_authenticated/files/")({
  component: FilesPage,
  validateSearch: (search: Record<string, unknown>): FilesSearch => ({
    section: parseSectionKey(search.section),
    share: optionalString(search.share),
  }),
  beforeLoad: async (args) => {
    await authGuardWithPermissions({
      context: args.context as RouterContextType,
      location: args.location as { pathname: string },
    });
  },
});

function FilesPage() {
  const { section, share } = Route.useSearch();
  const navigate = useNavigate();
  // The deep link is one-shot: once the hub has acted on it the URL goes back to a clean /files.
  const clearDeepLink = useCallback(() => {
    void navigate({ to: "/files", search: {}, replace: true });
  }, [navigate]);
  useBreadcrumb(
    [
      {
        title: "Files",
        href: "/files",
        icon: getRouteIcon(MODULE_KEYS_MAP.files, 16),
      },
    ],
    {},
  );

  return (
    <div className={styles.page}>
      <FilesHub
        deepLink={section || share ? { section, share } : undefined}
        onDeepLinkConsumed={clearDeepLink}
      />
    </div>
  );
}
